import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import QuickNearMissReport from '@/components/QuickNearMissReport';
import { 
  AlertTriangle,
  ArrowLeft,
  Plus,
  Calendar,
  MapPin
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const WorkerNearMiss = ({ user }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    try {
      // Workers use their own endpoint
      const response = await axiosInstance.get('/near-miss/my-reports');
      setReports(response.data);
    } catch (error) {
      console.error('Failed to load reports:', error);
      toast.error(t('nearMiss.reportFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleReportSuccess = () => {
    toast.success(t('nearMiss.reportSubmitted'));
    setShowReportDialog(false);
    loadReports(); // Reload reports
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'investigating': return 'bg-blue-100 text-blue-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('common.back')}
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('nearMiss.title')}</h1>
              <p className="text-gray-600 mt-1">{t('nearMiss.subtitle')}</p>
            </div>
          </div>
          <Button
            onClick={() => setShowReportDialog(true)}
            className="bg-yellow-500 hover:bg-yellow-600"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('nearMiss.reportNearMiss')}
          </Button>
        </div>

        {/* Info Card */}
        <Card className="mb-6 bg-yellow-50 border-yellow-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-yellow-600 mt-1" />
              <div>
                <h3 className="font-semibold text-yellow-900 mb-2">{t('nearMiss.whyReport')}</h3>
                <p className="text-sm text-yellow-800">
                  {t('nearMiss.whyReportDescription')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{t('incidents.totalReports')}</p>
                  <p className="text-2xl font-bold text-gray-900">{reports.length}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{t('jsa.acknowledged')}</p>
                  <p className="text-2xl font-bold text-green-600">
                    {reports.filter(r => r.acknowledgedBy).length}
                  </p>
                </div>
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                  <span className="text-green-600 font-bold">✓</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{t('common.submitted')}</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {reports.filter(r => r.status === 'submitted' && !r.acknowledgedBy).length}
                  </p>
                </div>
                <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center">
                  <span className="text-yellow-600 font-bold">⏱</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Reports List */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-900">{t('nearMiss.myReports')}</h2>
          
          {reports.length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-center py-12">
                <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">{t('nearMiss.noReports')}</p>
                <Button
                  onClick={() => setShowReportDialog(true)}
                  variant="outline"
                  className="border-yellow-500 text-yellow-600 hover:bg-yellow-50"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t('nearMiss.newReport')}
                </Button>
              </CardContent>
            </Card>
          ) : (
            reports.map((report) => (
              <Card key={report.id} className="hover:shadow-md transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg mb-2">{report.whatAlmostHappened || report.description}</CardTitle>
                      <div className="flex flex-wrap gap-2 mb-3">
                        <Badge variant="outline" className={getStatusColor(report.status)}>
                          {report.status}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Calendar className="w-4 h-4" />
                      <span>
                        {new Date(report.dateOccurred).toLocaleDateString()} at {report.timeOccurred}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <MapPin className="w-4 h-4" />
                      <span>{report.location}</span>
                    </div>
                  </div>
                  
                  {report.description && (
                    <div className="mt-4 p-3 bg-blue-50 rounded-md">
                      <p className="text-sm font-semibold text-blue-900 mb-1">{t('common.details')}:</p>
                      <p className="text-sm text-blue-800">{report.description}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Report Dialog */}
      <QuickNearMissReport 
        open={showReportDialog}
        onOpenChange={setShowReportDialog}
        onSuccess={handleReportSuccess}
      />
    </div>
  );
};

export default WorkerNearMiss;
